import React, { useState, useRef, useEffect } from 'react';
import AudioRecorder from './AudioRecorder';
import ImagePreview from './ImagePreview';
import { uploadMediaToS3 } from '../../Services/chatService';

const ChatInputBar = ({ onSend, onTyping, disabled }) => {
    const [text, setText]               = useState('');
    const [showAudio, setShowAudio]     = useState(false);
    const [imageFile, setImageFile]     = useState(null);
    const [uploading, setUploading]     = useState(false);
    const textareaRef                   = useRef(null);
    const fileInputRef                  = useRef(null);

    useEffect(() => {
        textareaRef.current?.focus();
    }, []);

    const handleTextChange = (e) => {
        setText(e.target.value);
        onTyping && onTyping();
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendText();
        }
    };

    const handleSendText = () => {
        const trimmed = text.trim();
        if (!trimmed || disabled) return;
        onSend({ messageType: 'text', content: trimmed });
        setText('');
    };

    // ── Image handling ────────────────────────────────────────────────────────
    const handleFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            alert('Please select an image file');
            return;
        }
        setImageFile(file);
    };

    const handleImageSend = async (file) => {
        if (!file || disabled) return;
        try {
            setUploading(true);
            const publicUrl = await uploadMediaToS3(file, 'image');
            onSend({
                messageType: 'image',
                content:     publicUrl,
                fileName:    file.name,
                fileSize:    file.size,
                mimeType:    file.type
            });
            setImageFile(null);
        } catch (e) {
            alert('Image upload failed: ' + e.message);
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    // ── Audio handling ────────────────────────────────────────────────────────
    const handleAudioSend = async (audioBlob, duration) => {
        if (!audioBlob || disabled) return;
        try {
            setUploading(true);
            const file = new File([audioBlob], `voice_${Date.now()}.webm`, { type: 'audio/webm' });
            const publicUrl = await uploadMediaToS3(file, 'audio');
            onSend({
                messageType:   'audio',
                content:       publicUrl,
                fileName:      file.name,
                fileSize:      file.size,
                audioDuration: duration,
                mimeType:      'audio/webm'
            });
            setShowAudio(false);
        } catch (e) {
            alert('Audio upload failed: ' + e.message);
        } finally {
            setUploading(false);
        }
    };

    if (disabled) return null;

    return (
        <div className="chat-input-bar">
            {/* Image Preview */}
            {imageFile && (
                <ImagePreview
                    file={imageFile}
                    onSend={() => handleImageSend(imageFile)}
                    onCancel={() => { setImageFile(null); fileInputRef.current.value = ''; }}
                    uploading={uploading}
                />
            )}

            {/* Audio Recorder */}
            {showAudio && (
                <AudioRecorder
                    onSend={handleAudioSend}
                    onCancel={() => setShowAudio(false)}
                    uploading={uploading}
                />
            )}

            {/* Main Input Row */}
            {!showAudio && !imageFile && (
                <div className="chat-input-row">
                    {/* Image attach */}
                    <button
                        className="chat-media-btn"
                        onClick={() => fileInputRef.current?.click()}
                        title="Send image"
                        disabled={uploading}
                    >
                        📷
                    </button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={handleFileSelect}
                    />

                    {/* Audio record */}
                    <button
                        className="chat-media-btn"
                        onClick={() => setShowAudio(true)}
                        title="Record voice note"
                        disabled={uploading}
                    >
                        🎙️
                    </button>

                    {/* Text input */}
                    <textarea
                        ref={textareaRef}
                        className="chat-text-input"
                        placeholder="Type a message... (Enter to send)"
                        value={text}
                        onChange={handleTextChange}
                        onKeyDown={handleKeyDown}
                        rows={1}
                        disabled={uploading}
                    />

                    {/* Send */}
                    <button
                        className={`chat-send-btn ${text.trim() ? 'chat-send-btn--active' : ''}`}
                        onClick={handleSendText}
                        disabled={!text.trim() || uploading}
                        title="Send message"
                    >
                        {uploading ? '⏳' : '➤'}
                    </button>
                </div>
            )}
        </div>
    );
};

export default ChatInputBar;
